import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import safeStorage from "@/lib/safeStorage";
import { toast } from "sonner";
import { Bell, Receipt, HelpCircle, MessageSquare, Plus, Minus, ShoppingBag, X, Check, Star, Globe } from "lucide-react";
import { makeT, SUPPORTED_LANGUAGES } from "@/lib/i18n";

function fmt(n) {
  return `€${Number(n).toFixed(2)}`;
}

export default function CustomerTable() {
  const { code } = useParams();
  const [table, setTable] = useState(null);
  const [menu, setMenu] = useState([]);
  const [cart, setCart] = useState({});
  const [cartOpen, setCartOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [settings, setSettings] = useState({ restaurant_name: "", google_review_url: "", language: "en" });
  const [activeTab, setActiveTab] = useState("Food");
  const [lightbox, setLightbox] = useState(null); // { src, alt } | null
  // Starting language always matches the restaurant admin's Settings.language.
  // Guests can switch to their own language during a session, but the choice
  // is intentionally NOT persisted so every new scan starts in the language
  // the venue admin chose. Falls back to English while settings are loading.
  const [langOverride, setLangOverride] = useState(null);
  const activeLang = langOverride || settings.language || "en";

  // Close the lightbox with Escape for keyboard users.
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  // Clean up any lingering `guest_lang` from older versions of the app so guests
  // don't carry a stale override on their device forever.
  useEffect(() => { try { safeStorage.removeItem("guest_lang"); } catch { /* noop */ } }, []);
  const t = useMemo(() => makeT(activeLang), [activeLang]);

  useEffect(() => {
    (async () => {
      try {
        const [t, m, s] = await Promise.all([
          api.get(`/public/table/${code}`),
          api.get(`/public/menu?table_code=${code}&lang=${activeLang}`),
          api.get(`/public/settings?table_code=${code}`),
        ]);
        setTable(t.data);
        setMenu(m.data);
        setSettings(s.data);
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // Re-fetch translated menu whenever the guest switches language
  useEffect(() => {
    if (!table) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/menu?table_code=${code}&lang=${activeLang}`);
        if (!cancelled) setMenu(data);
      } catch { /* keep previous */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLang, code]);

  const grouped = useMemo(() => {
    // Two-level grouping: section → category → items[]. Preserves the
    // server-side creation order within each category, and shows the two
    // top-level sections (Food, Drinks) in a fixed order regardless of
    // which items were added first.
    const bySection = { Food: {}, Drinks: {} };
    for (const it of menu) {
      const sec = it.section === "Drinks" ? "Drinks" : "Food";
      (bySection[sec][it.category] ||= []).push(it);
    }
    return bySection;
  }, [menu]);

  const cartItems = useMemo(
    () => menu.filter((i) => cart[i.id] > 0).map((i) => ({ ...i, qty: cart[i.id] })),
    [cart, menu]
  );
  const cartCount = cartItems.reduce((a, b) => a + b.qty, 0);
  const cartTotal = cartItems.reduce((a, b) => a + b.qty * b.price, 0);

  const inc = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const dec = (id) =>
    setCart((c) => {
      const n = (c[id] || 0) - 1;
      const nc = { ...c };
      if (n <= 0) delete nc[id]; else nc[id] = n;
      return nc;
    });

  const sendRequest = async (type, note = "") => {
    try {
      await api.post("/public/requests", { table_code: code, type, note });
      const map = {
        waiter: t("waiter_on_way"),
        bill: t("bill_being_prepared"),
        assistance: t("assistance_on_way"),
        other: t("request_sent"),
      };
      toast.success(map[type] || t("request_sent"));
    } catch (e) {
      toast.error(t("request_failed"));
    }
  };

  const placeOrder = async () => {
    if (!cartItems.length) return;
    try {
      const items = cartItems.map((i) => ({
        menu_item_id: i.id, name: i.name, description: i.description || "", price: i.price, quantity: i.qty,
      }));
      await api.post("/public/orders", { table_code: code, items, notes });
      toast.success(t("order_placed"));
      setCart({});
      setNotes("");
      setCartOpen(false);
    } catch (e) {
      toast.error(t("order_failed"));
    }
  };

  if (loading) {
    return (
      <div className="customer-scope min-h-screen flex items-center justify-center">
        <div className="text-[#5c605a] font-plex text-sm">{t("loading_table")}</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="customer-scope min-h-screen flex items-center justify-center px-8">
        <div className="text-center">
          <div className="overline text-[#c85a40]">{t("qr_invalid")}</div>
          <h1 className="font-serif-display text-4xl mt-3">{t("qr_invalid")}</h1>
          <p className="font-plex text-sm text-[#5c605a] mt-3">{t("qr_invalid_help")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-scope min-h-screen pb-40">
      {/* Sticky glass header */}
      <div className="sticky top-0 z-30 backdrop-blur-xl bg-[#f9f8f6]/80 border-b border-[#e5e1d8]">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="overline text-[#c85a40]">{t("now_serving")}</div>
            <div className="font-serif-display text-2xl leading-none mt-1 truncate">{table.label}</div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="inline-flex items-center gap-1.5 bg-[#f2efea] border border-[#e5e1d8] pl-2.5 pr-1 py-1.5 focus-within:border-[#1a1c19]" data-testid="lang-switcher-wrap">
              <Globe size={14} className="text-[#5c605a] shrink-0" />
              <select
                value={activeLang}
                onChange={(e) => setLangOverride(e.target.value)}
                aria-label="Language"
                data-testid="customer-lang-switcher"
                className="bg-transparent font-plex text-xs text-[#1a1c19] focus:outline-none appearance-none pr-4"
                style={{ backgroundImage: "none" }}
              >
                {SUPPORTED_LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
              </select>
            </div>
            <div className="text-right">
              <div className="overline text-[#5c605a]">{t("table")}</div>
              <div className="font-condensed text-3xl font-bold leading-none mt-1 tracking-tight">
                {String(table.number).padStart(2, "0")}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Google reviews CTA — TOP of page, above the menu */}
      {settings.google_review_url ? (
        <div className="max-w-2xl mx-auto px-6 pt-8">
          <div className="border border-[#e5e1d8] bg-[#f2efea] p-5 flex items-center gap-4 fade-up" data-testid="review-top-cta">
            <div className="w-11 h-11 shrink-0 inline-flex items-center justify-center border border-[#e5e1d8] bg-[#f9f8f6]">
              <Star size={20} className="text-[#c85a40]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="overline text-[#c85a40]">{t("loved_by_guests")}</div>
              <div className="font-serif-display text-xl leading-tight mt-1">{t("review_us_prompt")}</div>
            </div>
            <a
              href={settings.google_review_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-review-top-btn"
              className="shrink-0 hidden sm:inline-flex items-center gap-2 bg-[#1a1c19] text-[#f9f8f6] px-4 py-2.5 font-plex text-xs uppercase tracking-wide hover:bg-[#c85a40] transition-colors"
            >
              <Star size={14} /> {t("leave_google_review")}
            </a>
          </div>
          <a
            href={settings.google_review_url}
            target="_blank"
            rel="noopener noreferrer"
            className="sm:hidden mt-3 flex items-center justify-center gap-2 bg-[#1a1c19] text-[#f9f8f6] py-3 font-plex text-xs uppercase tracking-wide"
          >
            <Star size={14} /> {t("leave_google_review")}
          </a>
        </div>
      ) : null}

      {/* Quick actions — sit between reviews and menu */}
      <div className="max-w-2xl mx-auto px-6 pt-10">
        <div className="overline text-[#5c605a]">{t("assistance")}</div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <ActionButton icon={<Bell size={18} />} label={t("call_waiter")} onClick={() => sendRequest("waiter")} testId="call-waiter-btn" />
          <ActionButton icon={<Receipt size={18} />} label={t("request_bill")} onClick={() => sendRequest("bill")} testId="request-bill-btn" />
          <ActionButton icon={<HelpCircle size={18} />} label={t("need_help")} onClick={() => sendRequest("assistance")} testId="request-assistance-btn" />
          <ActionButton icon={<MessageSquare size={18} />} label={t("other_request")} onClick={() => {
            const note = window.prompt(t("other_request"));
            if (note && note.trim()) sendRequest("other", note.trim());
          }} testId="other-request-btn" />
        </div>
      </div>

      {/* Menu */}
      <div className="max-w-2xl mx-auto px-6 pt-12">
        <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight">{t("the_menu")}</h2>

        {/* Tabs */}
        <div className="mt-8 flex border-b border-[#e5e1d8]" role="tablist" data-testid="menu-tabs">
          {["Food", "Drinks"].map((tab) => {
            const isActive = activeTab === tab;
            const hasItems = Object.keys(grouped[tab] || {}).length > 0;
            return (
              <button
                key={tab}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab)}
                disabled={!hasItems}
                data-testid={`menu-tab-${tab.toLowerCase()}`}
                className={`flex-1 py-3 font-plex text-sm uppercase tracking-wide transition-colors ${
                  isActive
                    ? "text-[#1a1c19] border-b-2 border-[#1a1c19] -mb-px"
                    : "text-[#5c605a] hover:text-[#1a1c19]"
                } ${!hasItems ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                {t(tab === "Food" ? "section_food" : "section_drinks")}
              </button>
            );
          })}
        </div>

        <div className="mt-10 space-y-12" data-testid={`menu-panel-${activeTab.toLowerCase()}`}>
          {Object.keys(grouped[activeTab] || {}).map((cat) => (
                    <section key={cat} className="fade-up">
                      <div className="flex items-baseline gap-4 mb-6">
                        <h4 className="font-serif-display text-2xl">{cat}</h4>
                        <div className="flex-1 h-px bg-[#e5e1d8]" />
                      </div>
                      <ul className="space-y-6">
                        {grouped[activeTab][cat].map((item) => (
                          <li key={item.id} className="group flex gap-4" data-testid={`menu-item-${item.id}`}>
                            {item.has_image ? (
                              <button
                                type="button"
                                onClick={() => setLightbox({ src: `${API}/public/menu-item/${item.id}/image`, alt: item.name })}
                                aria-label={item.name}
                                data-testid={`menu-item-image-btn-${item.id}`}
                                className="shrink-0 focus:outline-none focus:ring-2 focus:ring-[#c85a40]"
                              >
                                <img
                                  src={`${API}/public/menu-item/${item.id}/image`}
                                  alt={item.name}
                                  loading="lazy"
                                  data-testid={`menu-item-image-${item.id}`}
                                  className="w-24 h-24 sm:w-28 sm:h-28 object-cover border border-[#e5e1d8] cursor-zoom-in transition-transform hover:scale-[1.02]"
                                />
                              </button>
                            ) : null}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline">
                                <div className="font-serif-display text-xl text-[#1a1c19]">{item.name}</div>
                                <div className="dot-leader" />
                                <div className="font-plex text-base text-[#1a1c19] tabular-nums">{fmt(item.price)}</div>
                              </div>
                              {item.description ? (
                                <p className="font-plex text-sm text-[#5c605a] mt-1.5 max-w-lg leading-relaxed">
                                  {item.description}
                                </p>
                              ) : null}
                              <div className="mt-3 flex items-center gap-3">
                                {cart[item.id] ? (
                                  <div className="flex items-center gap-3 border border-[#1a1c19] px-3 py-1.5">
                                    <button onClick={() => dec(item.id)} data-testid={`dec-${item.id}`} className="text-[#1a1c19]"><Minus size={14} /></button>
                                    <span className="font-plex text-sm w-4 text-center tabular-nums">{cart[item.id]}</span>
                                    <button onClick={() => inc(item.id)} data-testid={`inc-${item.id}`} className="text-[#1a1c19]"><Plus size={14} /></button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => inc(item.id)}
                                    data-testid={`add-to-cart-btn-${item.id}`}
                                    className="border border-[#1a1c19] px-3 py-1.5 font-plex text-xs tracking-wide uppercase hover:bg-[#1a1c19] hover:text-[#f9f8f6] transition-colors"
                                  >
                                    {t("add")}
                                  </button>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ))}
        </div>

        {settings.google_review_url ? null : null}
      </div>

      {/* Cart floating bar */}
      {cartCount > 0 && (
        <div className="fixed bottom-4 left-4 right-4 z-40 max-w-2xl mx-auto">
          <button
            onClick={() => setCartOpen(true)}
            data-testid="open-cart-btn"
            className="w-full bg-[#1a1c19] text-[#f9f8f6] px-5 py-4 flex items-center justify-between hover:bg-[#c85a40] transition-colors"
          >
            <span className="flex items-center gap-3 font-plex text-sm">
              <ShoppingBag size={18} />
              {cartCount} {cartCount > 1 ? t("item_plural") : t("item_singular")} · {fmt(cartTotal)}
            </span>
            <span className="font-plex text-sm tracking-wide">{t("review_order")}</span>
          </button>
        </div>
      )}

      {/* Cart sheet */}
      {cartOpen && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40" onClick={() => setCartOpen(false)}>
          <div className="bg-[#f9f8f6] w-full md:max-w-lg md:rounded-none p-6 md:p-8 max-h-[85vh] overflow-y-auto border-t md:border border-[#e5e1d8]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <div className="overline text-[#c85a40]">{t("your_order")}</div>
                <div className="font-serif-display text-3xl">{table.label}</div>
              </div>
              <button onClick={() => setCartOpen(false)} data-testid="close-cart-btn" className="text-[#5c605a]"><X size={20} /></button>
            </div>
            <ul className="mt-6 divide-y divide-[#e5e1d8]">
              {cartItems.map((i) => (
                <li key={i.id} className="py-4 flex items-baseline gap-4">
                  <div className="flex-1">
                    <div className="font-serif-display text-lg">{i.name}</div>
                    <div className="font-plex text-xs text-[#5c605a] mt-0.5">{fmt(i.price)} {t("each")}</div>
                  </div>
                  <div className="flex items-center gap-2 border border-[#1a1c19] px-2 py-1">
                    <button onClick={() => dec(i.id)}><Minus size={12} /></button>
                    <span className="font-plex text-sm w-4 text-center tabular-nums">{i.qty}</span>
                    <button onClick={() => inc(i.id)}><Plus size={12} /></button>
                  </div>
                  <div className="font-plex text-sm w-16 text-right tabular-nums">{fmt(i.qty * i.price)}</div>
                </li>
              ))}
            </ul>
            <div className="mt-4">
              <label className="overline text-[#5c605a]">{t("notes_for_kitchen")}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder={t("notes_placeholder")}
                data-testid="order-notes"
                className="mt-2 w-full bg-transparent border border-[#e5e1d8] p-3 font-plex text-sm focus:outline-none focus:border-[#1a1c19]"
              />
            </div>
            <div className="mt-6 flex items-baseline justify-between border-t border-[#e5e1d8] pt-4">
              <div className="overline text-[#5c605a]">{t("total")}</div>
              <div className="font-serif-display text-3xl tabular-nums">{fmt(cartTotal)}</div>
            </div>
            <button
              onClick={placeOrder}
              data-testid="place-order-btn"
              className="mt-6 w-full bg-[#1a1c19] text-[#f9f8f6] py-4 flex items-center justify-center gap-3 hover:bg-[#c85a40] transition-colors"
            >
              <Check size={18} /> <span className="font-plex text-sm tracking-wide">{t("send_to_kitchen")}</span>
            </button>
          </div>
        </div>
      )}
      {/* Fullscreen image lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 fade-up"
          onClick={() => setLightbox(null)}
          data-testid="image-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            aria-label="Close"
            data-testid="image-lightbox-close"
            className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 backdrop-blur transition-colors"
          >
            <X size={22} />
          </button>
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            onClick={(e) => e.stopPropagation()}
            data-testid="image-lightbox-img"
            className="max-w-[95vw] max-h-[90vh] object-contain shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}

function ActionButton({ icon, label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="group flex items-center gap-3 border border-[#e5e1d8] bg-[#f2efea] px-4 py-4 hover:border-[#1a1c19] hover:bg-[#1a1c19] hover:text-[#f9f8f6] transition-colors text-left"
    >
      <span className="text-[#c85a40] group-hover:text-[#f9f8f6] transition-colors">{icon}</span>
      <span className="font-plex text-sm">{label}</span>
    </button>
  );
}
